import {computed, makeObservable, observable} from "mobx";
import {JsonObject, JsonProperty} from "typescript-json-serializer";
import {AbstractModel} from "./abstract-model";


export interface PartialOrderProps {
    objectId?: number;
    guid?: string;
    type?: PartialOrderType;
    projectId?: number;
    riskEvidenceEvaluationId?: number;
    riskEvaluationId?: number;
    recurrencePeriodId?: string;
    primaryProcessTypeId?: number;
    sideProcessTypeId?: number;
    processSourceName?: string;
    creator?: string;
    createdAt?: Date;
    editor?: string;
    updatedAt?: Date;
}

export type PartialOrderType = "partial-order-gk" | "partial-order-ghk" | "partial-order-risk";

/**
 * Aufträge im Rahmen des Projekts auf Beurteilungsstufe Gefahrenkarte auszuführen sind.
 * Die Aufträge beinhalten Angaben zu den zu beurteilenden Haupt- und Nebenprozessen, Wiederkehrperioden und
 * allenfalls Prozessquellen
 */
@JsonObject()
export class PartialOrder extends AbstractModel {

    static readonly comparableAttributesGhk: string[] = ["riskEvidenceEvaluationId"];
    static readonly comparableAttributesGk: string[] = [
        "processSourceName", "riskEvaluationId", "primaryProcessTypeId", "sideProcessTypeId", "recurrencePeriodId"
    ];
    static readonly comparableAttributesRisk: string[] = [
        "processSourceName", "riskEvaluationId", "primaryProcessTypeId", "sideProcessTypeId", "recurrencePeriodId"
    ];
    static readonly prototypeAttributes: string[] = [
        "OBJECTID", "GUID", "PROJ_ID", "PA_GHK", "PQ_NR_GK", "GB", "HPA", "PA", "WKP_PERI",
        "CREATED_USER", "CREATED_AT", "UPDATED_USER", "UPDATED_AT"
    ];
    // field mappings
    static readonly databaseFieldNames: Map<string, string> = new Map<string, string>([
        ["objectId", "OBJECTID"],
        ["guid", "GUID"],
        ["projectId", "PROJ_ID"],
        ["riskEvidenceEvaluationId", "PA_GHK"],
        ["processSourceName", "PQ_NR_GK"],
        ["riskEvaluationId", "GB"],
        ["primaryProcessTypeId", "HPA"],
        ["sideProcessTypeId", "PA"],
        ["recurrencePeriodId", "WKP_PERI"],
        ["creator", "CREATED_USER"],
        ["createdAt", "CREATED_AT"],
        ["editor", "UPDATED_USER"],
        ["updatedAt", "UPDATED_AT"]
    ]);

    class = "PartialOrder";
    typeLabel = "Teilauftrag";

    type: PartialOrderType;

    /**
     * Unique identifier
     * TODO: Add GUID to database
     */
    @JsonProperty({name: "GUID"})
        guid: string;

    /**
     * Projektschlüssel des zugehörigen Projekts
     */
    @JsonProperty({name: "PROJ_ID"})
        projectId: number;

    /**
     * Umfang Gefahrenhinweisbeurteilung
     * Domäne PA_GHK
     */
    @JsonProperty({name: "PA_GHK"})
        riskEvidenceEvaluationId: number;

    /**
     * Umfang Gefahrenbeurteilung
     * Domäne GB
     */
    @JsonProperty({name: "GB"})
        riskEvaluationId: number;

    /**
     * Zu untersuchende Widerkehrperioden
     * Domäne WKP_PERI
     */
    @JsonProperty({name: "WKP_PERI"})
        recurrencePeriodId: string;

    /**
     * Zu untersuchende Hauptprozesse
     * Domäne HPA
     */
    @JsonProperty({name: "HPA"})
        primaryProcessTypeId: number;

    /**
     * Beschränkung auf Nebenprozessart
     */
    @JsonProperty({name: "PA"})
        sideProcessTypeId: number;

    /**
     * Beschränkung auf Prozessquelle
     *
     */
    @JsonProperty({name: "PQ_NR_GK"})
        processSourceName: string;


    constructor(props?: PartialOrderProps) {
        super();
        if (props) {
            Object.assign(this, props);
        }
        // make observable after setting properties to avoid 'strict' messages
        makeObservable(this, {
            objectId: observable, // do observe to reload when object is saved
            guid: observable,
            projectId: observable,
            type: observable,
            riskEvidenceEvaluationId: observable,
            riskEvaluationId: observable,
            recurrencePeriodId: observable,
            primaryProcessTypeId: observable,
            sideProcessTypeId: observable,
            processSourceName: observable,

            isAlterable: computed,

            creator: observable,
            createdAt: observable,
            editor: observable,
            updatedAt: observable,
            isNewRecord: computed
        });
    }

    get comparableAttributes(): string[] {
        return PartialOrder.comparableAttributes;
    }

    comparableAttributesForType(): string[] {
        switch (this.type) {
        case "partial-order-gk":
            return PartialOrder.comparableAttributesGk;
        case "partial-order-ghk":
            return PartialOrder.comparableAttributesGhk;
        case "partial-order-risk":
            return PartialOrder.comparableAttributesGk;
        default:
            return [];
        }
    }

    get isAlterable(): boolean {
        return true;
    }

}
